import { Component, OnInit, ViewChild, Input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { AlertService } from 'shared/services/alert.service';
import { PreloaderService } from 'shared/services/preloader.service';
import { UtilityService } from 'shared/services/utility.service';
import { ExportToExcelService } from 'shared/services/export-to-excel.service';
import { PhoenixApiService } from 'shared/services/phoenix-api.service';
import { EngaugeAIService } from 'shared/services/engauge-ai.service';

@Component({
  selector: 'app-engauge-ai-data',
  templateUrl: './engauge-ai-data.component.html',
  styleUrls: ['./engauge-ai-data.component.css']
})
export class EngaugeAiDataComponent implements OnInit {

  @Input()
  set jobNumber(e: any) {
    if (e) {
      this._jobNum = e;
      this.pageTitle = `AI Outcome for Job ${e}`;
      this.loadTransactionData();
    }
  }

  @Input()
  set jobId(e: any) {
    if (e) {
      this._jobId = e;
      this.loadData();
      this.loadTransactionData();
    }
  }

  _jobNum = '';
  _jobId: any = null;
  pageTitle = 'AI Outcome for Job';
  exportTitle = 'AI Outcome for Job';

  tableColumns: any[] = [];
  transColumns: any[] = [];
  tableData: any[] = [];
  transactionData: any[] = [];
  filteredItems: any[] = [];
  transFilteredItems: any[] = [];
  loadingIndicator = false;
  transLoadingIndicator = false;
  itemsPerPage = 10;
  totalCount = 0;
  transTotalCount = 0;
  localFilters: any = {};
  transLocalFilters: any = {};
  itemsPerBatch = 200;
  noRecords = false;
  dataList: any[] = [];
  transDataList: any[] = [];
  isLoadMore = false;
  isTransLoadMore = false;
  datePipe: DatePipe = new DatePipe('en-US');
  @ViewChild('myTable') table: any;
  @ViewChild('transTable') transTable: any;

  constructor(
    private _phoenixApiService: PhoenixApiService,
    private _engaugeAIService: EngaugeAIService,
    private _loader: PreloaderService,
    private _alertService: AlertService,
    private _utilityService: UtilityService,
    private _exportToExcelService: ExportToExcelService
  ) { }

  ngOnInit() {
  }

  initColumns() {
    this.tableColumns = [
      { name: 'Reference Id', prop: 'referenceId', width: 166, visible: true, sortable: true, type: 'text' },
      { name: 'Flow Type', prop: 'flowType', width: 209, visible: true, sortable: true, type: 'text' },
      { name: 'Last Response At', prop: 'lastResponseAt', width: 173, visible: true, sortable: true, type: 'text' },
      { name: 'Status', prop: 'status', width: 250, visible: true, sortable: true, type: 'text' },
      { name: 'Stakeholders', prop: 'stakeholders', width: 172, visible: true, sortable: true, type: 'text' },
      { name: 'Attempts', prop: 'attempts', width: 148, visible: true, sortable: true, type: 'text' },
      { name: 'Created At', prop: 'createdAt', width: 214, visible: true, sortable: true, type: 'date' }
    ];

    this.transColumns = [
      { name: 'Channel', prop: 'channel', width: 205, visible: true, sortable: true, type: 'text' },
      { name: 'Correlation ID', prop: 'correlationId', width: 307, visible: true, sortable: true, type: 'text' },
      { name: 'Status', prop: 'transStatus', width: 152, visible: true, sortable: true, type: 'text' },
      { name: 'Response Time (ms)', prop: 'responseTime', width: 220, visible: true, sortable: true, type: 'text' },
      { name: 'Request At', prop: 'requestAt', width: 230, visible: true, sortable: true, type: 'date' },
      { name: 'Response At', prop: 'responseAt', width: 215, visible: true, sortable: true, type: 'date' },
    ];
  }

  loadData() {
    this.initColumns();
    this.loadingIndicator = true;
    this._loader.showPreloader();

    this._phoenixApiService.getData(`/jobs/${this._jobId}/outcome`)
      .then(result => {
        const data = result && result.data ? (Array.isArray(result.data) ? result.data : result.data['data'] || []) : [];
        this.totalCount = (result && result.data && result.data['total']) ? +result.data['total'] : data.length;
        this.noRecords = data.length < this.itemsPerBatch;

        const prepared = this.prepareData(data);
        if (this.isLoadMore) {
          this.dataList = [...this.dataList, ...prepared];
        } else {
          this.dataList = [...prepared];
        }
        this.filteredItems = this.dataList.slice();

        if (Object.keys(this.localFilters).length) {
          this.tableData = this.filteredItems.filter(item => {
            const notMatchingField = Object.keys(this.localFilters).find(key =>
              this._utilityService.dataTableSearch(item, this.localFilters, key));
            return !notMatchingField;
          });
        } else {
          this.tableData = [...this.dataList];
          if (!this.isLoadMore && this.table) {
            this.table['offset'] = 0;
          }
        }

        this.loadingIndicator = false;
        this.isLoadMore = false;
        this.setEmptyMessage();
        this._loader.hidePreloader();
      })
      .catch(err => {
        this._alertService.error((err.response && err.response.data && err.response.data.message) || err.message || 'Server Error');
        this.setEmptyMessage();
        this.loadingIndicator = false;
        this._loader.hidePreloader();
      });
  }

  prepareData(data: any[]) {
    if (data && data.length) {
      data.forEach(item => {
        item['referenceId'] = item.referenceId || '';
        item['flowType'] = item.flowType || '';
        item['lastResponseAt'] = item.lastResponseAt;
        item['status'] = item.status || '';
        item['stakeholders'] = item.stakeholders || '';
        item['attempts'] = item.attempts || '';
        item['createdAt'] = item.createdAt ? this.transform(item.createdAt, 'MM/dd/yyyy, h:mm a') : '';
      });
      return data;
    }
    return [];
  }

  transform(value: string, type?: any) {
    return value ? new DatePipe('en-US').transform(value, type || 'short') : value;
  }

  setEmptyMessage() {
    const msg = 'No data to display.';
    if (this.tableData && !this.tableData.length) {
      this.tableData = [{ 'message': msg, 'jobNumber': msg }];
    } else if (this.tableData && this.tableData[0] && this.tableData[0].hasOwnProperty('message')) {
      this.tableData.shift();
    }
  }

  applyLocalFilters(event: any, type: any) {
    if (type === 'date') {
      if (event.value === '') {
        delete this.localFilters[event.input.id];
      } else {
        const date = this.transform(event.value).split(',');
        this.localFilters[event.input.id] = date[0];
      }
    } else {
      if (event.target.value === '') {
        delete this.localFilters[event.currentTarget.id];
      } else {
        this.localFilters[event.currentTarget.id] = event.target.value;
      }
    }
    this.tableData = this.filteredItems.filter(item => {
      const notMatchingField = Object.keys(this.localFilters).find(key =>
        this._utilityService.dataTableSearch(item, this.localFilters, key));
      return !notMatchingField;
    });
    this.noRecords = this.tableData.length < this.itemsPerBatch;
    if (this.table) { this.table['offset'] = 0; }
    this.setEmptyMessage();
  }

  clearLocalFilters(col: any) {
    delete this.localFilters[col];
    this.tableData = this.filteredItems.filter(item => {
      const notMatchingField = Object.keys(this.localFilters).find(key =>
        this._utilityService.dataTableSearch(item, this.localFilters, key));
      return !notMatchingField;
    });
    this.noRecords = this.tableData.length < this.itemsPerBatch;
    if (this.table) { this.table['offset'] = 0; }
    this.setEmptyMessage();
  }

  onRefresh() {
    this.loadData();
  }

  onRefreshTrans() {
    this.loadTransactionData();
  }

  loadTransactionData() {
    this.transLoadingIndicator = true;
    this._loader.showPreloader();

    this._engaugeAIService.postData(`user/analytics/transaction-history`, { 
        "agentType":"TPMO",
        "orderBy":"responseAt",
        "sortDirection":"desc",
        "externalCd": this._jobNum })
      .then((result: any) => {
        const data = result && result.data ? (Array.isArray(result.data) ? result.data : result.data['data'] || []) : [];
        this.transTotalCount = (result && result.data && result.data['total']) ? +result.data['total'] : data.length;

        const prepared = this.prepareTransactionData(data);
        if (this.isTransLoadMore) {
          this.transDataList = [...this.transDataList, ...prepared];
        } else {
          this.transDataList = [...prepared];
        }
        this.transFilteredItems = this.transDataList.slice();

        if (Object.keys(this.transLocalFilters).length) {
          this.transactionData = this.transFilteredItems.filter(item => {
            const notMatchingField = Object.keys(this.transLocalFilters).find(key =>
              this._utilityService.dataTableSearch(item, this.transLocalFilters, key));
            return !notMatchingField;
          });
        } else {
          this.transactionData = [...this.transDataList];
          if (!this.isTransLoadMore && this.transTable) {
            this.transTable['offset'] = 0;
          }
        }

        this.transLoadingIndicator = false;
        this.isTransLoadMore = false;
        this.setTransEmptyMessage();
        this._loader.hidePreloader();
      })
      .catch(err => {
        this._alertService.error((err.response && err.response.data && err.response.data.message) || err.message || 'Server Error');
        this.setTransEmptyMessage();
        this.transLoadingIndicator = false;
        this._loader.hidePreloader();
      });
  }

  prepareTransactionData(data: any[]) {
    if (data && data.length) {
      data.forEach(item => {
        item['channel'] = item.channel || '';
        item['correlationId'] = item.correlationId || '';
        item['transStatus'] = item.status || '';
        item['responseTime'] = item.responseTime !== undefined ? item.responseTime : '';
        item['requestAt'] = item.requestAt ? this.transform(item.requestAt, 'MM/dd/yyyy, h:mm a') : '';
        item['responseAt'] = item.responseAt ? this.transform(item.responseAt, 'MM/dd/yyyy, h:mm a') : '';
      });
      return data;
    }
    return [];
  }

  setTransEmptyMessage() {
    const msg = 'No data to display.';
    if (this.transactionData && !this.transactionData.length) {
      this.transactionData = [{ 'message': msg, 'channel': msg }];
    } else if (this.transactionData && this.transactionData[0] && this.transactionData[0].hasOwnProperty('message')) {
      this.transactionData.shift();
    }
  }

  applyTransLocalFilters(event: any, type: any) {
    if (type === 'date') {
      if (event.value === '') {
        delete this.transLocalFilters[event.input.id];
      } else {
        const date = this.transform(event.value).split(',');
        this.transLocalFilters[event.input.id] = date[0];
      }
    } else {
      if (event.target.value === '') {
        delete this.transLocalFilters[event.currentTarget.id];
      } else {
        this.transLocalFilters[event.currentTarget.id] = event.target.value;
      }
    }
    this.transactionData = this.transFilteredItems.filter(item => {
      const notMatchingField = Object.keys(this.transLocalFilters).find(key =>
        this._utilityService.dataTableSearch(item, this.transLocalFilters, key));
      return !notMatchingField;
    });
    if (this.transTable) { this.transTable['offset'] = 0; }
    this.setTransEmptyMessage();
  }

  clearTransLocalFilters(col: any) {
    delete this.transLocalFilters[col];
    this.transactionData = this.transFilteredItems.filter(item => {
      const notMatchingField = Object.keys(this.transLocalFilters).find(key =>
        this._utilityService.dataTableSearch(item, this.transLocalFilters, key));
      return !notMatchingField;
    });
    if (this.transTable) { this.transTable['offset'] = 0; }
    this.setTransEmptyMessage();
  }

  exportToExcel() {
    this._exportToExcelService.exportToExcel(this.tableData, this.tableColumns, this.exportTitle);
  }
}
